import { useParams } from 'react-router-dom'
import { ComponentWithPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { InputTableURLsTable } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import { ColumnOrderTableSchema } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ITDiscountDisplay, ITTypeQuantityDisplay } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../specificComponents/Product/types'
import locations from '../../../../../../../../../utils/globals/locations'
import { SellInvoiceProductModelTypeExtended } from '@teinor/erp/types/company/order/sell/sellInvoice/sellInvoiceProduct'
import { sellInvoiceRefundStatusInversed } from '@teinor/erp/types/company/order/sell/sellInvoiceRefund'
import useMultiOrderRefundLogic from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/useMultiOrderRefundLogic'
import { parseSelectedTableDataForRefund } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/useMultiOrderRefundLogic/functions'
import OrderSellLayout from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout'
import MultiOrderRefundButton from '../../../../../../../../../specificComponents/sells/components/multi/MultiOrderRefundButton'
import MultiOrderRefundITHeaderConnect from '../../../../../../../../../specificComponents/sells/components/multi/MultiOrderRefundITHeader'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import { WorkingCenterPayload } from '../../../../../../../../../utils/reducers/company/workingCenters'

type CreateMultiSellInvoicesRefundProps = ComponentWithPermissions & ReduxCreateMultiSellInvoicesRefund & {
}
interface SellInvoiceProductModelTypeExtendedWithRefund extends SellInvoiceProductModelTypeExtended {
    restRefundQuantity: number
    totalRefund: boolean,
    returnStock: boolean
}
const CreateMultiSellInvoicesRefund: React.FC<CreateMultiSellInvoicesRefundProps> = ({ userPermissions, workingCenters }) => {
    const { t } = useTwinTranslation()
    const { ids } = useParams()
    const parseIds = ids?.split('-')
    const inputTableURLs: InputTableURLsTable = {
        createData: ['', {}],
        getData: ['/api/app/order/sell/sellInvoice/getMultiSellInvoiceComplete', { id: parseIds || [] }],
        updateData: '',
        updateHeaderData: '/api/app/order/sell/sellInvoiceRefund/updateSellInvoiceRefund',
        getSubProducts: ['/api/app/order/sell/sellInvoice/orderProduct/getAllSubSellInvoiceProducts', ['CustomerId']],
        updateOrderLines: '',
        delete: '',
        printTags: ''
    }
    const { completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration } = useMultiOrderRefundLogic({ inputTableURLs, userPermissions, type: 'sellInvoiceHeader', workingCenters })
    const dictWarehouses = workingCenters?.[completeData?.header?.WorkingCenterId]?.dictWarehouses
    if(!completeData){
        return null
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellInvoiceProductModelTypeExtendedWithRefund> = [
        {
            id: 'selected',
            dataKey: 'selected',
            label: '',
            simpleLabel: t('selectLine', 'Seleccionar línea'),
            width: 50,
            requiredColumn: true,
            component: 'ITCheckbox',
            headerClassName: 'checkbox_header_filter',
            extraProps: { readOnlyFnc: (allRowData: any) => allRowData?.totalRefund },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: { 'unprocessed': { name: t('unprocessed', 'Sin procesar') }, 'processed': { name: t('processed', 'Procesados') } }, label: t('show', 'Mostrar') },
            }
        },
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchProductInput',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            sortable: true,
            minWidth: 180,
            className: 'quote_order_reference_name'
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className='twin_elipsis' />
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            label: () => <div className='text-right mr-15 w-full'>{t('price', 'Precio')}</div>,
            simpleLabel: t('price', 'Precio'),
            component: 'ITDisplayPrice',
            width: 100,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            width: 110,
            component: 'ITInputQuantityRefund',
            typeOnChange: 'onChangeTotals',
            applyMaxValue: true,
        },
        {
            id: 'restRefundQuantity',
            dataKey: 'restRefundQuantity',
            label: t('invoicedRestQuantityShort', 'Cant. rest.'),
            simpleLabel: t('invoicedRestQuantity', 'Cantidad restante'),
            toolTip: t('invoicedRestQuantity', 'Cantidad restante'),
            width: 110,
            component: 'ITInputQuantityRefund',
            readOnlyFnc: () => true
        },
        {
            id: 'desiredQuantity',
            dataKey: 'desiredQuantity',
            label: t('invoicedQuantityShort', 'Cant. fact.'),
            simpleLabel: t('invoicedQuantity', 'Cantidad facturada'),
            toolTip: t('invoicedQuantity', 'Cantidad facturada'),
            width: 110,
            component: 'ITInputQuantity',
            readOnlyFnc: () => true
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity2} value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            width: 110,
            component: 'ITInputQuantityRefund',
            typeOnChange: 'onChangeTotals',
            applyMaxValue: true,
            readOnlyFnc: () => true
        },
        {
            id: 'discount',
            dataKey: 'discount',
            label: t('discount', 'Descuento'),
            width: 80,
            customRender: (parameterValue, allRowData) => <ITDiscountDisplay CurrencyId={completeData?.header.CurrencyId} value={parameterValue} typeDiscount={allRowData.typeDiscount} decimals={2} />
        },
        {
            id: 'rowPriceWithoutTaxPrev',
            dataKey: 'rowPriceWithoutTaxPrev',
            label: () => <div className='text-right mr-15 w-full'>{t('rowPrice', 'Precio Línea')}</div>,
            simpleLabel: t('rowPrice', 'Precio Línea'),
            component: 'ITDisplayFinalPrice',
            hideColumnByDefault: true,
            with_vat: false,
            width: 120
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('priceTotal', 'Precio Total')}</div>,
            simpleLabel: t('priceTotal', 'Precio Total'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('totalPriceWithTaxShort', 'Precio Total Imp.')}</div>,
            simpleLabel: t('totalPriceWithTaxShort', 'Precio Total Imp.'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 130,
            hideColumnByDefault: true,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            component: 'ITDisplayPrice',
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalWithComplements', 'Total con complementos'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplements', 'Total con complementos')
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'Nº único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProductReadOnly',
            hideColumnByDefault: true,
        },
        {
            id: 'returnStock',
            dataKey: 'returnStock',
            label: t('returnStockShort', 'Dev. stock'),
            simpleLabel: t('returnStock', 'Devolver stock'),
            toolTip: t('returnStock', 'Devolver stock'),
            component: 'ITOrderBooleanRectangle',
            hideColumnByDefault: true,
            translations: { positive: t('yes', 'Sí'), negative: t('no', 'No') },
            typeOnChange: 'onChange',
            width: 105,
        },
        {
            id: 'WarehouseId',
            dataKey: 'WarehouseId',
            label: t('warehouse', 'Almacén'),
            component: 'ITInputSelectWarehouses',
            minWidth: 110,
            hideColumnByDefault: true,
            items: dictWarehouses || [],
            readOnlyFnc: (allRowData) => allRowData?.processed,
            typeOnChange: 'onChange'
        },
    ]
    return (
        <OrderSellLayout modelId='SellInvoiceRefundId' translations={{ headerTitle: t('generateInvoiceRefund', 'Generar devolución de factura') }} hideLeftFooter={true} hideNotes={true} name='CreateMultiSellInvoicesRefund' orderStatusDict={sellInvoiceRefundStatusInversed} buttonSave={<MultiOrderRefundButton data={completeData} urlProcess='/api/app/order/sell/sellInvoiceRefund/createSellInvoiceRefundFromMultiInvoices' translationsModal={{
            title: t('createUniqueInvoiceRefund', 'Crear devolución de factura'),
            subtitle: t('youSureAboutInvoiceRefundCreation', '¿Estás seguro que quieres crear una devolución de factura con los productos seleccionados?')
        }} {...{ userPermission: getUserPermissions('sell.refundInvoice'), urlNavigate: locations.sellInvoiceRefundSingle, setCompleteData }} />
        } InputTableHeaderChildren={MultiOrderRefundITHeaderConnect} ITHeaderChildrenExtraData={{
            translateText: { titleTab: t('dataRefund', 'Datos de la devolución') },
            urlNavigate: locations.sellInvoiceRefundMulti,
            completeData,
        }} {...{ columns, inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, orderHeaderURLs: {} as any, userPermissions, title: t('multiSellInvoiceRefund', 'Devolución de factura múltiple'), customParseTableData: parseSelectedTableDataForRefund }} />
    )
}

const CreateMultiSellInvoicesRefundDispatch = {
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
}

export type ReduxCreateMultiSellInvoicesRefund = ConnectedProps<typeof createMultiSellInvoicesRefundConnect>
const mapCreateMultiSellInvoicesRefundConnector = (state: AllReduxPayloads) => ({ workingCenters: state.workingCenters })
const createMultiSellInvoicesRefundConnect = connect(mapCreateMultiSellInvoicesRefundConnector, CreateMultiSellInvoicesRefundDispatch)
const CreateMultiSellInvoicesRefundConnectLoading = withLoading(CreateMultiSellInvoicesRefund, [{ fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }])
const CreateMultiSellInvoicesRefundConnect = createMultiSellInvoicesRefundConnect(CreateMultiSellInvoicesRefundConnectLoading)

export default CreateMultiSellInvoicesRefundConnect